import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query'
import { operatorDailyLogsApi } from '@/lib/api'
import type {
  OperatorDailyLog,
  OperatorDailyLogFormData,
  OperatorDailyLogSummary,
} from '@/types'

export const operatorDailyLogKeys = {
  all: ['operator-daily-logs'] as const,
  byDate: (date: string) => [...operatorDailyLogKeys.all, date] as const,
  summaries: () => [...operatorDailyLogKeys.all, 'summary'] as const,
  summary: (date: string, month: string) =>
    [...operatorDailyLogKeys.summaries(), date, month] as const,
}

export function useOperatorDailyLog(date: string): UseQueryResult<OperatorDailyLog | null> {
  return useQuery({
    queryKey: operatorDailyLogKeys.byDate(date),
    queryFn: async () => {
      const res = await operatorDailyLogsApi.getByDate(date)
      return res.data ?? null
    },
    enabled: Boolean(date),
  })
}

export function useOperatorDailyLogSummary(
  date: string,
  month: string
): UseQueryResult<OperatorDailyLogSummary> {
  return useQuery({
    queryKey: operatorDailyLogKeys.summary(date, month),
    queryFn: async () => {
      const res = await operatorDailyLogsApi.summary({ date, month })
      return res.data
    },
    enabled: Boolean(date && month),
  })
}

export function useUpsertOperatorDailyLog(): UseMutationResult<
  OperatorDailyLog,
  unknown,
  OperatorDailyLogFormData
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: OperatorDailyLogFormData) => {
      const res = await operatorDailyLogsApi.upsert(data)
      return res.data
    },
    onSuccess: (log) => {
      queryClient.setQueryData(operatorDailyLogKeys.byDate(log.date), log)
      queryClient.invalidateQueries({ queryKey: operatorDailyLogKeys.summaries() })
    },
  })
}
